import { X, Heart, Star, ExternalLink, Coffee, Gift, Sparkles } from 'lucide-react';
import { Button } from './ui/Button';

interface SupportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SupportModal: React.FC<SupportModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Heart className="w-6 h-6" />
              <h2 className="text-xl font-bold">Support RPBedu</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Main Support Card */}
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-6 border border-blue-100 dark:border-blue-800">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  Support RPBedu
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Help us keep building free education tools
                </p>
              </div>
            </div>

            <p className="text-gray-600 dark:text-gray-300 mb-4">
              You can support RPBedu by as little as <span className="font-bold text-blue-600 dark:text-blue-400">$1 per month</span> and receive upgrades, rewards, and our eternal gratitude!
            </p>

            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Your support helps us:
            </p>
            <ul className="space-y-2 mb-4">
              {[
                'Keep the servers running',
                'Develop new AI features',
                'Add more flashcard templates',
                'Provide faster AI generation',
                'Maintain and improve the app',
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                  <Star className="w-4 h-4 text-yellow-500" />
                  {item}
                </li>
              ))}
            </ul>

            <a
              href="https://www.patreon.com/cw/RPBedu"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Button className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white">
                <Coffee className="w-4 h-4 mr-2" />
                Become a Patreon Supporter
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>

          {/* Reward Tiers */}
          <div>
            <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
              Support Tiers
            </h4>
            <div className="grid grid-cols-3 gap-3">
              {[
                { amount: '$1', tier: 'Friend', icon: Star },
                { amount: '$5', tier: 'Supporter', icon: Gift },
                { amount: '$10', tier: 'Champion', icon: Heart },
              ].map((item) => (
                <div
                  key={item.tier}
                  className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center"
                >
                  <item.icon className="w-6 h-6 mx-auto mb-2 text-orange-500" />
                  <div className="font-bold text-gray-900 dark:text-gray-100">
                    {item.amount}/mo
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {item.tier}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Other Ways to Help */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-4 border border-yellow-200 dark:border-yellow-800">
            <h4 className="text-sm font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
              Other Ways to Support
            </h4>
            <ul className="space-y-2 text-sm text-yellow-700 dark:text-yellow-300">
              <li>• Share the app with friends and classmates</li>
              <li>• Leave a positive review</li>
              <li>• Report bugs and suggest features</li>
              <li>• Contribute flashcards and study materials</li>
            </ul>
          </div>

          {/* Thank You */}
          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            Thank you for using FlashcardGeneratorAI! ❤️
          </p>
        </div>
      </div>
    </div>
  );
};

export default SupportModal;